import React, { useState, useEffect } from 'react';
import productAPI from '../../services/productApi.js'; 
import './ProductAdmin.css';

const ProductAdmin = ({ setCurrentPage }) => {
  const [formData, setFormData] = useState({
    type: '',
    name: '',
    description: '',
    short_description: '',
    price: '',
    is_on_sale: false,
    sale_price: '',
    images: [], 
    category_id: '',
    features: [''],
    tags: [''],
    stock_quantity: '',
    track_inventory: true,
    is_active: true,
    is_featured: false,
    meta_title: '',
    meta_description: '',
    pricing_model: 'fixed',
    duration: '',
    is_available: true
  });

  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploadedImagePaths, setUploadedImagePaths] = useState([]);
  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      console.log('Loading categories...');
      const response = await productAPI.getCategories({ active_only: true });
      
      console.log('Full response:', response);
      
      // Your API returns: { success: true, data: { categories: [...], count: N }, message: "..." }
      if (response && response.data && response.data.categories && Array.isArray(response.data.categories)) {
        console.log('Categories loaded successfully:', response.data.categories);
        setCategories(response.data.categories);
      } else {
        console.warn('Unexpected response structure:', response);
        // Fallback
        setCategories([]);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      const errorInfo = productAPI.handleAPIError(error);
      setErrors({ api: errorInfo.message });
      setCategories([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (name === 'is_on_sale' && !checked) {
      setFormData(prev => ({
        ...prev,
        sale_price: ''
      }));
    }

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    if (success) {
      setSuccess('');
    }
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    if (value === 'add-new') {
      setShowAddCategory(true);
      setFormData(prev => ({
        ...prev,
        category_id: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        category_id: value
      }));
      setShowAddCategory(false);
    }

    if (errors.category_id) {
      setErrors(prev => ({
        ...prev,
        category_id: ''
      }));
    }
  };

  const handleAddCategory = async () => {
    setErrors(prev => ({
      ...prev,
      newCategory: ''
    }));

    if (!newCategory.trim()) {
      setErrors(prev => ({
        ...prev,
        newCategory: 'Category name is required'
      }));
      return;
    }

    try {
      setLoading(true);
      
      const categoryData = {
        name: newCategory.trim()
      };

      console.log('Creating category with data:', categoryData);
      const response = await productAPI.createCategory(categoryData);
      
      console.log('Category creation response:', response);
      
      // Your API returns: { success: true, data: { category: {...} }, message: "..." }
      if (response && response.success) {
        // Get the newly created category - it's in response.data.category
        const newCategoryObj = response.data && response.data.category;
        
        if (newCategoryObj && newCategoryObj.id) {
          console.log('Category created successfully:', newCategoryObj);
          
          setCategories(prev => [...prev, newCategoryObj]);
          
          setFormData(prev => ({
            ...prev,
            category_id: newCategoryObj.id.toString()
          }));
          
          setNewCategory('');
          setShowAddCategory(false);
          setSuccess('Category added successfully!');
          
          setTimeout(() => setSuccess(''), 3000);
        } else {
          throw new Error('Invalid category response structure');
        }
      } else {
        throw new Error(response?.message || 'Failed to create category');
      }
    } catch (error) {
      console.error('Category creation error:', error);
      console.error('Full error details:', {
        message: error.message,
        stack: error.stack
      });
      
      const errorInfo = productAPI.handleAPIError(error);
      const errorMsg = errorInfo.message || 'Failed to add category';
      
      setErrors(prev => ({
        ...prev,
        newCategory: errorMsg
      }));
      
      alert(`Error adding category: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAddCategory = () => {
    setNewCategory('');
    setShowAddCategory(false);
    setFormData(prev => ({
      ...prev,
      category_id: ''
    }));
    setErrors(prev => ({
      ...prev,
      newCategory: ''
    }));
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const validFiles = [];
    const invalidFiles = [];

    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        if (file.size <= 16 * 1024 * 1024) {
          validFiles.push(file);
        } else {
          invalidFiles.push(`${file.name} (too large)`);
        }
      } else {
        invalidFiles.push(`${file.name} (not an image)`);
      }
    });

    if (invalidFiles.length > 0) {
      setErrors(prev => ({
        ...prev,
        images: `Invalid files: ${invalidFiles.join(', ')}`
      }));
    }

    if (validFiles.length === 0) return;

    try {
      setLoading(true);

      const result = await productAPI.uploadFiles(validFiles);
      
      if (result.success) {
        const newPaths = result.data?.files || result.files || [];
        setUploadedImagePaths(prev => [...prev, ...newPaths]);

        const newPreviews = [...imagePreviews];
        
        validFiles.forEach((file, index) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            newPreviews.push({
              file: file,
              preview: reader.result,
              name: file.name,
              path: newPaths[index] || file.name
            });
            setImagePreviews([...newPreviews]);
          };
          reader.readAsDataURL(file);
        });

        setErrors(prev => {
          const { images, ...rest } = prev;
          return rest;
        });
      } else {
        throw new Error(result.message || 'Upload failed');
      }

    } catch (error) {
      console.error('Upload error:', error);
      const errorInfo = productAPI.handleAPIError(error);
      setErrors(prev => ({
        ...prev,
        images: errorInfo.message || 'Failed to upload images'
      }));

      if (errorInfo.shouldRedirect) {
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const removeImage = (index) => {
    const newPreviews = imagePreviews.filter((_, i) => i !== index);
    const newPaths = uploadedImagePaths.filter((_, i) => i !== index);
    
    setImagePreviews(newPreviews);
    setUploadedImagePaths(newPaths);
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData(prev => ({
      ...prev,
      features: newFeatures
    }));
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const removeFeature = (index) => {
    if (formData.features.length > 1) {
      const newFeatures = formData.features.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        features: newFeatures
      }));
    }
  };

  const handleTagChange = (index, value) => {
    const newTags = [...formData.tags];
    newTags[index] = value;
    setFormData(prev => ({
      ...prev,
      tags: newTags
    }));
  };

  const addTag = () => {
    setFormData(prev => ({
      ...prev,
      tags: [...prev.tags, '']
    }));
  };

  const removeTag = (index) => {
    if (formData.tags.length > 1) {
      const newTags = formData.tags.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        tags: newTags
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.type) {
      newErrors.type = 'Please select a type';
    }
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Valid price is required';
    }

    if (formData.type === 'product') {
      if (uploadedImagePaths.length === 0) {
        newErrors.images = 'At least one image is required for products';
      }
      if (formData.track_inventory && (!formData.stock_quantity || parseInt(formData.stock_quantity) < 0)) {
        newErrors.stock_quantity = 'Valid stock quantity is required when tracking inventory';
      }
    }

    if (formData.is_on_sale) {
      if (!formData.sale_price || parseFloat(formData.sale_price) <= 0) {
        newErrors.sale_price = 'Valid sale price is required';
      } else if (parseFloat(formData.sale_price) >= parseFloat(formData.price)) {
        newErrors.sale_price = 'Sale price must be less than original price';
      }
    }

    if (formData.type === 'service') {
      const validPricingModels = ['fixed', 'hourly', 'project-based'];
      if (!validPricingModels.includes(formData.pricing_model)) {
        newErrors.pricing_model = 'Invalid pricing model';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const prepareSubmissionData = () => {
    const baseData = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      short_description: formData.short_description.trim() || undefined,
      price: parseFloat(formData.price),
      is_on_sale: formData.is_on_sale,
      sale_price: formData.is_on_sale ? parseFloat(formData.sale_price) : undefined,
      category_id: formData.category_id ? parseInt(formData.category_id) : undefined,
      features: productAPI.processFeatures(formData.features),
      tags: productAPI.processTags(formData.tags),
      images: uploadedImagePaths,
      is_active: formData.is_active,
      is_featured: formData.is_featured,
      meta_title: formData.meta_title.trim() || undefined,
      meta_description: formData.meta_description.trim() || undefined
    };

    if (formData.type === 'product') {
      return {
        ...baseData,
        stock_quantity: formData.track_inventory ? parseInt(formData.stock_quantity) || 0 : 0,
        track_inventory: formData.track_inventory
      };
    } else {
      return {
        ...baseData,
        pricing_model: formData.pricing_model,
        duration: formData.duration.trim() || undefined,
        is_available: formData.is_available
      };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setErrors({});
      
      const submissionData = prepareSubmissionData();
      
      let response;
      if (formData.type === 'product') {
        response = await productAPI.createProduct(submissionData);
      } else {
        response = await productAPI.createService(submissionData);
      }

      if (response && response.success) {
        setSuccess(`${formData.type === 'product' ? 'Product' : 'Service'} created successfully!`);
        resetForm();
      }
    } catch (error) {
      const errorInfo = productAPI.handleAPIError(error);
      setErrors({ submit: errorInfo.message });
      
      if (errorInfo.shouldRedirect) {
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      type: '',
      name: '',
      description: '',
      short_description: '',
      price: '',
      is_on_sale: false,
      sale_price: '',
      images: [],
      category_id: '',
      features: [''],
      tags: [''],
      stock_quantity: '',
      track_inventory: true,
      is_active: true,
      is_featured: false,
      meta_title: '',
      meta_description: '',
      pricing_model: 'fixed',
      duration: '',
      is_available: true
    });
    setImagePreviews([]);
    setUploadedImagePaths([]);
    setErrors({});
    setShowAddCategory(false);
    setNewCategory('');
    setSuccess('');
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div className="header-content">
          <h1>Admin Panel</h1>
          <p>Add New Product or Service</p>
        </div>
        <div className="header-actions">
          <button 
            className="btn btn-secondary manage-btn"
            onClick={() => setCurrentPage('admin-management')}
          >
            📊 Manage Inventory
          </button>
        </div>
      </div>

      {success && (
        <div className="success-message">
          {success}
        </div>
      )}

      {errors.submit && (
        <div className="error-message-global">
          {errors.submit}
        </div>
      )}

      {errors.api && (
        <div className="error-message-global">
          {errors.api}
        </div>
      )}

      <form onSubmit={handleSubmit} className="admin-form">
        <div className="form-group">
          <label className="form-label">Type *</label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="type"
                value="product"
                checked={formData.type === 'product'}
                onChange={handleInputChange}
                disabled={loading}
              />
              <span className="radio-custom"></span>
              Product
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="type"
                value="service"
                checked={formData.type === 'service'}
                onChange={handleInputChange}
                disabled={loading}
              />
              <span className="radio-custom"></span>
              Service
            </label>
          </div>
          {errors.type && <span className="error-message">{errors.type}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={`form-input ${errors.name ? 'error' : ''}`}
            placeholder={`Enter ${formData.type} name`}
            disabled={loading}
          />
          {errors.name && <span className="error-message">{errors.name}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">Category</label>
          <select
            name="category_id"
            value={showAddCategory ? 'add-new' : formData.category_id}
            onChange={handleCategoryChange}
            className={`form-input category-select ${errors.category_id ? 'error' : ''}`}
            disabled={loading}
          >
            <option value="">Select a category</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
            <option value="add-new">+ Add New Category</option>
          </select>
          {errors.category_id && <span className="error-message">{errors.category_id}</span>}
        </div>

        {showAddCategory && (
          <div className="form-group add-category-section">
            <label className="form-label">New Category Name</label>
            <div className="add-category-row">
              <input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                className={`form-input ${errors.newCategory ? 'error' : ''}`}
                placeholder="Enter new category name"
                disabled={loading}
              />
              <button
                type="button"
                onClick={handleAddCategory}
                className="btn-add-category"
                disabled={loading}
              >
                {loading ? 'Adding...' : 'Add'}
              </button>
              <button
                type="button"
                onClick={handleCancelAddCategory}
                className="btn-cancel-category"
                disabled={loading}
              >
                Cancel
              </button>
            </div>
            {errors.newCategory && <span className="error-message">{errors.newCategory}</span>}
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Description *</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            className={`form-textarea ${errors.description ? 'error' : ''}`}
            placeholder={`Enter ${formData.type} description`}
            rows={4}
            disabled={loading}
          />
          {errors.description && <span className="error-message">{errors.description}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">Short Description</label>
          <textarea
            name="short_description"
            value={formData.short_description}
            onChange={handleInputChange}
            className="form-textarea"
            placeholder="Enter brief description (optional)"
            rows={2}
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Features</label>
          {formData.features.map((feature, index) => (
            <div key={index} className="feature-row">
              <input
                type="text"
                value={feature}
                onChange={(e) => handleFeatureChange(index, e.target.value)}
                className="form-input"
                placeholder={`Feature ${index + 1}`}
                disabled={loading}
              />
              {formData.features.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeFeature(index)}
                  className="btn-remove"
                  disabled={loading}
                >
                  ×
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addFeature}
            className="btn-add-feature"
            disabled={loading}
          >
            + Add Feature
          </button>
        </div>

        <div className="form-group">
          <label className="form-label">Tags</label>
          {formData.tags.map((tag, index) => (
            <div key={index} className="feature-row">
              <input
                type="text"
                value={tag}
                onChange={(e) => handleTagChange(index, e.target.value)}
                className="form-input"
                placeholder={`Tag ${index + 1}`}
                disabled={loading}
              />
              {formData.tags.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeTag(index)}
                  className="btn-remove"
                  disabled={loading}
                >
                  ×
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addTag}
            className="btn-add-feature"
            disabled={loading}
          >
            + Add Tag
          </button>
        </div>

        <div className="form-group">
          <label className="form-label">Price *</label>
          <input
            type="number"
            name="price"
            value={formData.price}
            onChange={handleInputChange}
            className={`form-input ${errors.price ? 'error' : ''}`}
            placeholder="Enter price"
            min="0"
            step="0.01"
            disabled={loading}
          />
          {errors.price && <span className="error-message">{errors.price}</span>}
        </div>

        {formData.type === 'service' && (
          <>
            <div className="form-group">
              <label className="form-label">Pricing Model</label>
              <select
                name="pricing_model"
                value={formData.pricing_model}
                onChange={handleInputChange}
                className="form-input"
                disabled={loading}
              >
                <option value="fixed">Fixed Price</option>
                <option value="hourly">Hourly Rate</option>
                <option value="project-based">Project Based</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Duration</label>
              <input
                type="text"
                name="duration"
                value={formData.duration}
                onChange={handleInputChange}
                className="form-input"
                placeholder="e.g., 2-3 hours, 1 week"
                disabled={loading}
              />
            </div>
          </>
        )}

        {formData.type === 'product' && (
          <>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="track_inventory"
                  checked={formData.track_inventory}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                <span className="checkbox-custom"></span>
                Track Inventory
              </label>
            </div>

            {formData.track_inventory && (
              <div className="form-group">
                <label className="form-label">Stock Quantity *</label>
                <input
                  type="number"
                  name="stock_quantity"
                  value={formData.stock_quantity}
                  onChange={handleInputChange}
                  className={`form-input ${errors.stock_quantity ? 'error' : ''}`}
                  placeholder="Enter stock quantity"
                  min="0"
                  disabled={loading}
                />
                {errors.stock_quantity && <span className="error-message">{errors.stock_quantity}</span>}
              </div>
            )}
          </>
        )}

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="is_on_sale"
              checked={formData.is_on_sale}
              onChange={handleInputChange}
              disabled={loading}
            />
            <span className="checkbox-custom"></span>
            On Sale
          </label>
        </div>

        {formData.is_on_sale && (
          <div className="form-group sale-price-group">
            <label className="form-label">Sale Price *</label>
            <input
              type="number"
              name="sale_price"
              value={formData.sale_price}
              onChange={handleInputChange}
              className={`form-input ${errors.sale_price ? 'error' : ''}`}
              placeholder="Enter sale price"
              min="0"
              step="0.01"
              disabled={loading}
            />
            {errors.sale_price && <span className="error-message">{errors.sale_price}</span>}
          </div>
        )}

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleInputChange}
              disabled={loading}
            />
            <span className="checkbox-custom"></span>
            Active
          </label>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="is_featured"
              checked={formData.is_featured}
              onChange={handleInputChange}
              disabled={loading}
            />
            <span className="checkbox-custom"></span>
            Featured
          </label>
        </div>

        {formData.type === 'service' && (
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_available"
                checked={formData.is_available}
                onChange={handleInputChange}
                disabled={loading}
              />
              <span className="checkbox-custom"></span>
              Available for Booking
            </label>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Meta Title</label>
          <input
            type="text"
            name="meta_title"
            value={formData.meta_title}
            onChange={handleInputChange}
            className="form-input"
            placeholder="SEO title (optional)"
            maxLength="160"
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Meta Description</label>
          <textarea
            name="meta_description"
            value={formData.meta_description}
            onChange={handleInputChange}
            className="form-textarea"
            placeholder="SEO description (optional)"
            maxLength="320"
            rows={2}
            disabled={loading}
          />
        </div>

        {(formData.type === 'product' || formData.type === 'service') && (
          <div className="form-group">
            <label className="form-label">
              Images {formData.type === 'product' ? '*' : '(Optional)'}
            </label>
            
            <div className="image-upload-container">
              <input
                type="file"
                id="imageInput"
                accept="image/*"
                multiple
                onChange={handleImageChange}
                className="image-input"
                disabled={loading}
              />
              <label htmlFor="imageInput" className="image-upload-label">
                <div className="image-placeholder">
                  <span>📸</span>
                  <p>{loading ? 'Uploading...' : 'Click to upload images'}</p>
                  <small>You can select multiple images (max 16MB each)</small>
                </div>
              </label>
            </div>

            {imagePreviews.length > 0 && (
              <div className="image-previews-container">
                <h4>Uploaded Images ({imagePreviews.length})</h4>
                <div className="image-previews-grid">
                  {imagePreviews.map((imageData, index) => (
                    <div key={index} className="image-preview-item">
                      <img 
                        src={imageData.preview} 
                        alt={`Preview ${index + 1}`} 
                        className="image-preview-thumbnail"
                      />
                      <div className="image-preview-overlay">
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="btn-remove-image"
                          title="Remove image"
                          disabled={loading}
                        >
                          ×
                        </button>
                      </div>
                      <p className="image-name">{imageData.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {errors.images && <span className="error-message">{errors.images}</span>}
          </div>
        )}

        <div className="form-actions">
          <button 
            type="button" 
            onClick={resetForm} 
            className="btn-secondary"
            disabled={loading}
          >
            Reset
          </button>
          <button 
            type="submit" 
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : `Add ${formData.type || 'Item'}`}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductAdmin;